<template>
  <article class="relative pl-16 pb-8">
    <div class="timeline-line absolute left-6 top-12 bottom-0 w-px bg-gray-200" aria-hidden="true" />

    <div
      class="absolute left-0 top-0 flex h-12 w-12 items-center justify-center rounded-full"
      :class="iconWrapperClass"
      aria-hidden="true"
    >
      <component :is="iconComponent" class="h-5 w-5" :class="iconClass" />
    </div>

    <div
      class="flex flex-col gap-3 rounded-xl border border-transparent px-1 py-1 md:flex-row md:items-start md:justify-between"
    >
      <div class="min-w-0 flex-1">
        <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm leading-5">
          <span class="font-medium text-gray-900">{{ item.actor || 'System' }}</span>
          <span class="text-gray-500">{{ activityVerb }}</span>
        </div>

        <div
          v-if="bodyPreview"
          class="activity-preview mt-1.5 text-sm leading-6"
          :class="bodyPreviewClass"
          v-html="sanitizeRichText(bodyPreview)"
        />

        <div v-if="detailLine" class="mt-1.5 text-sm text-gray-500">
          {{ detailLine }}
        </div>

        <div class="mt-2.5 flex flex-wrap items-center gap-3 text-sm">
          <Button
            variant="ghost"
            size="sm"
            class="h-auto px-0 text-gray-500 hover:bg-transparent hover:text-gray-900"
            @click="detailsOpen = !detailsOpen"
          >
            {{ detailsOpen ? 'Hide details' : 'View details' }}
          </Button>
          <template v-for="buttonItem in primaryButtons" :key="buttonItem.title">
            <Button
              v-if="buttonItem.action.onClick"
              variant="link"
              size="sm"
              class="h-auto px-0 text-gray-900"
              @click="buttonItem.action.onClick"
            >
              {{ buttonItem.title }}
            </Button>
            <Button
              v-else-if="buttonItem.action.emitId"
              variant="link"
              size="sm"
              class="h-auto px-0 text-gray-900"
              @click="emit(buttonItem.action.emitId)"
            >
              {{ buttonItem.title }}
            </Button>
            <Button
              v-else-if="buttonItem.action.href"
              as="a"
              :href="buttonItem.action.href"
              target="_blank"
              rel="noreferrer"
              variant="link"
              size="sm"
              class="h-auto px-0 text-gray-900"
            >
              {{ buttonItem.title }}
            </Button>
          </template>
          <DropdownMenu v-if="item.dropdownOptions?.length">
            <DropdownMenuTrigger as-child>
              <Button variant="ghost" size="icon" class="size-7 text-gray-400 hover:bg-gray-50 hover:text-gray-700">
                <vue-feather type="more-horizontal" size="14" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                v-for="option in item.dropdownOptions"
                :key="option.title"
                class="cursor-pointer"
                @select="handleOptionSelect(option)"
              >
                <vue-feather v-if="option.icon" :type="option.icon" size="14" class="mr-2 h-4 w-4" />
                <span>{{ option.title }}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Transition name="activity-details">
          <div v-if="detailsOpen" class="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-4">
            <dl class="grid gap-4 sm:grid-cols-2">
              <div>
                <dt class="text-xs font-medium uppercase tracking-wide text-gray-500">Type</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ detailTypeLabel }}</dd>
              </div>
              <div v-if="item.actor" class="sm:col-span-2">
                <dt class="text-xs font-medium uppercase tracking-wide text-gray-500">Actor</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ item.actor }}</dd>
              </div>
              <div
                v-for="contentItem in detailContent"
                :key="`${contentItem.label ?? 'details'}-${contentItem.richText}`"
                class="sm:col-span-2"
              >
                <dt class="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {{ contentItem.label || 'Details' }}
                </dt>
                <dd
                  class="activity-full-content mt-2 text-sm leading-6 text-gray-700"
                  v-html="sanitizeRichText(contentItem.richText ?? '')"
                />
              </div>
            </dl>
            <div v-if="destructiveButtons.length" class="mt-4 flex justify-end border-t border-gray-200 pt-4">
              <template v-for="buttonItem in destructiveButtons" :key="buttonItem.title">
                <Button
                  v-if="buttonItem.action.onClick"
                  variant="ghost"
                  size="sm"
                  class="h-auto px-0 text-red-500 hover:bg-transparent hover:text-red-600"
                  @click="buttonItem.action.onClick"
                >
                  {{ normalizeButtonLabel(buttonItem.title) }}
                </Button>
                <Button
                  v-else-if="buttonItem.action.emitId"
                  variant="ghost"
                  size="sm"
                  class="h-auto px-0 text-red-500 hover:bg-transparent hover:text-red-600"
                  @click="emit(buttonItem.action.emitId)"
                >
                  {{ normalizeButtonLabel(buttonItem.title) }}
                </Button>
              </template>
            </div>
          </div>
        </Transition>
      </div>

      <TooltipProvider :delay-duration="0">
        <Tooltip>
          <TooltipTrigger as-child>
            <div class="shrink-0 text-sm text-gray-400 md:pl-6">
              {{ formattedTime }}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {{ createdAt }}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, ref, type Component } from 'vue';
import { Button } from '@/components/ui/button';
import { sanitizeRichText } from '@/utilities/sanitization.ts';
import type {
  ActionOption,
  ActivityIconName,
  ActivityItem,
  ActivityPreviewEmitEvent,
} from './Transformers/useActivityTransformer';
import DateUtility from '@/utilities/dates';
import {
  CalendarDaysIcon,
  ClipboardPenLineIcon,
  GitCommitVerticalIcon,
  MailIcon,
  MessageSquareIcon,
  PhoneCallIcon,
  UserRoundCheckIcon,
  WorkflowIcon,
} from 'lucide-vue-next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const iconComponents: Record<ActivityIconName, Component> = {
  mail: MailIcon,
  'phone-call': PhoneCallIcon,
  'calendar-days': CalendarDaysIcon,
  'user-round-check': UserRoundCheckIcon,
  'message-square': MessageSquareIcon,
  workflow: WorkflowIcon,
  'clipboard-pen-line': ClipboardPenLineIcon,
  'git-commit-vertical': GitCommitVerticalIcon,
};

const iconStyles: Record<ActivityIconName, { wrapperClass: string; iconClass: string }> = {
  mail: { wrapperClass: 'bg-blue-50', iconClass: 'text-blue-600' },
  'phone-call': { wrapperClass: 'bg-emerald-50', iconClass: 'text-emerald-600' },
  'calendar-days': { wrapperClass: 'bg-violet-50', iconClass: 'text-violet-600' },
  'user-round-check': { wrapperClass: 'bg-gray-100', iconClass: 'text-gray-600' },
  'message-square': { wrapperClass: 'bg-blue-50', iconClass: 'text-blue-600' },
  workflow: { wrapperClass: 'bg-slate-100', iconClass: 'text-slate-600' },
  'clipboard-pen-line': { wrapperClass: 'bg-amber-50', iconClass: 'text-amber-600' },
  'git-commit-vertical': { wrapperClass: 'bg-emerald-50', iconClass: 'text-emerald-600' },
};

const emit = defineEmits<{ (e: ActivityPreviewEmitEvent): void }>();

const { item } = defineProps<{
  item: ActivityItem;
}>();

const detailsOpen = ref(false);

const formattedTime = computed(() => DateUtility.fromNowWithSuffix(item.displayTimestamp));

const iconComponent = computed(() => iconComponents[item.icon]);
const iconWrapperClass = computed(() => iconStyles[item.icon].wrapperClass);
const iconClass = computed(() => iconStyles[item.icon].iconClass);
const activityVerb = computed(() => item.verb);

const detailContent = computed(() => item.content.filter(contentItem => !!contentItem.richText));

const summaryBodyItem = computed(() => {
  const unlabeled = detailContent.value.find(contentItem => !contentItem.label);
  if (unlabeled) return unlabeled;

  return detailContent.value.find(contentItem => contentItem.label !== 'Status') ?? detailContent.value[0];
});

const bodyPreview = computed(() => {
  const richText = summaryBodyItem.value?.richText;
  if (!richText) return '';
  return truncateHtml(richText, 160);
});

const detailLine = computed(() => {
  const labeled = detailContent.value.find(
    contentItem => contentItem.label && contentItem !== summaryBodyItem.value && contentItem.label !== 'Status'
  );
  if (!labeled?.richText) return '';
  return `${labeled.label}: ${stripHtml(labeled.richText)}`;
});

const bodyPreviewClass = 'text-gray-600';

const primaryButtons = computed(() => (item.buttons ?? []).filter(buttonItem => !isDestructive(buttonItem)));
const destructiveButtons = computed(() => (item.buttons ?? []).filter(buttonItem => isDestructive(buttonItem)));

const createdAt = computed(() => DateUtility.formatDate(item.displayTimestamp, 'MMMM d, yyyy h:mm a') ?? '');

const detailTypeLabel = computed(() => item.typeLabel);

const normalizeButtonLabel = (label: string) => {
  if (label === 'Delete note') return 'Delete';
  return label;
};

const isDestructive = (buttonItem: ActionOption) => /delete/i.test(buttonItem.title);

const handleOptionSelect = (option: ActionOption) => {
  if (option.action.onClick) option.action.onClick();
  if (option.action.emitId) emit(option.action.emitId);
  if (option.action.href) window.open(option.action.href, '_blank');
};

const stripHtml = (html: string) =>
  html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const truncateHtml = (html: string, maxLength: number) => {
  const plainText = stripHtml(html);
  if (plainText.length <= maxLength) return plainText;
  return `${plainText.slice(0, maxLength).trimEnd()}...`;
};
</script>

<style scoped>
@reference "tailwindcss";

.activity-preview:deep(p) {
  @apply my-0;
}

.activity-preview:deep(p + p) {
  @apply mt-2;
}

.activity-preview:deep(br) {
  @apply hidden;
}

.activity-full-content:deep(p) {
  @apply my-0;
}

.activity-full-content:deep(p + p) {
  @apply mt-3;
}

:deep(.activity-details-enter-active),
:deep(.activity-details-leave-active) {
  transition:
    opacity 180ms ease,
    transform 180ms ease,
    max-height 220ms ease;
  overflow: hidden;
}

:deep(.activity-details-enter-from),
:deep(.activity-details-leave-to) {
  opacity: 0;
  transform: translateY(-6px);
  max-height: 0;
}

:deep(.activity-details-enter-to),
:deep(.activity-details-leave-from) {
  opacity: 1;
  transform: translateY(0);
  max-height: 28rem;
}
</style>
